import { useEffect } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Seo from "@/components/Seo";
import { Navbar } from "../components/landing/Navbar";
import { Footer } from "../components/landing/Footer";
import { SEO_PAGES } from "../data/seoPages";
import { SUPPORTED_LOCALES } from "../data/i18n";

// A locale-prefixed URL whose English twin exists (e.g. /ar/swiss-alps-tours)
// redirects to the English page instead of showing a blank screen.
const englishTwin = (pathname) => {
  const parts = pathname.split("/").filter(Boolean);
  if (!SUPPORTED_LOCALES.includes(parts[0]) || parts.length < 2) return null;
  const rest = parts.slice(1).join("/");
  const known =
    SEO_PAGES.some((p) => p.slug === rest) || /^(tours|fleet|blog)(\/[^/]+)?$/.test(rest);
  return known ? `/${rest}` : null;
};

export const NotFoundPage = () => {
  const { pathname } = useLocation();
  const twin = englishTwin(pathname);

  useEffect(() => {
    const el = document.createElement("meta");
    el.setAttribute("name", "robots");
    el.setAttribute("content", "noindex, follow");
    document.head.appendChild(el);
    return () => el.remove();
  }, []);

  if (twin) return <Navigate to={twin} replace />;

  return (
    <div className="min-h-screen flex flex-col" data-testid="not-found-page">
      <Seo title="Page Not Found | Voyage Switzerland" siteName="Voyage Switzerland" />
      <Navbar />
      <div className="flex-1 flex flex-col items-center justify-center gap-5 px-5 pt-32 pb-20 text-center">
        <h1 className="font-display font-bold text-3xl sm:text-4xl text-[#0b132b]" data-testid="not-found-heading">
          Page not found
        </h1>
        <p className="text-slate-600 max-w-md">
          The page you're looking for doesn't exist or may have moved. Explore our private and luxury tours across
          Switzerland instead.
        </p>
        <Link
          to="/"
          data-testid="not-found-home-link"
          className="inline-flex items-center gap-2 rounded-full bg-[var(--brand)] text-white text-sm font-bold px-6 py-3 shadow-lg shadow-[var(--brand)]/30 hover:bg-[var(--brand-dark)] transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back to homepage
        </Link>
      </div>
      <Footer />
    </div>
  );
};
